@import '../../style/index';

$navigation-pre-height: rem-calc(54px);
$navigation-main-height-mobile: rem-calc(64px);
$navigation-main-height-desktop: rem-calc(96px);

$navigation-border: 1px solid var(--colors-grey-medium);

:host {
  .skip-link {
    /* Remove item from normal flow */
    position: absolute;
    top: 8px;
    left: 45%;
    z-index: 1000;
  }

  .skip-link:not(:focus) {
    white-space: nowrap;
    width: 1px;
    height: 1px;
    overflow: hidden;
    border: 0;
    padding: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    margin: -1px;
  }

  .navigation {
    @include layer-highest-priority;

    position: relative;

    &__pre {
      display: none;

      @include xlarge-up {
        position: relative;
        border-bottom: $navigation-border;
        background: var(--colors-basic-white);
        height: $navigation-pre-height;
        display: flex;
        align-items: center;

        @include layer-highest-priority-top;
      }

      &-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      }
    }

    &__group {
      display: flex;
      align-items: center;
      gap: rem-calc(26px);
    }

    &__logo {
      display: block;
      padding-left: var(--spacings-s);
      cursor: pointer;

      @include xlarge-up {
        padding-left: 0;
      }
    }

    &__logo-40-years {
      width: rem-calc(40px);
      margin-left: rem-calc(17px);

      @include xlarge-up {
        margin-left: 0;
        width: rem-calc(60px);
      }
    }

    &__right {
      display: flex;
    }

    &__button {
      position: relative;
      display: flex;
      gap: var(--spacings-xs);
      background: transparent;
      justify-content: center;
      align-items: center;
      color: var(--colors-dark-base);
      font-family: $font-family-semibold;
      font-size: rem-calc(16px);
      line-height: 1;
      cursor: pointer;
      user-select: none;
      border: 0;
      text-decoration: none;
      margin-top: rem-calc(21px);

      @include xlarge-up {
        margin-top: 0;
      }

      &__icon {
        background: var(--colors-primary-base);
        border-radius: 50%;
        height: rem-calc(26px);
        width: rem-calc(26px);
        display: flex;

        rcm-icon {
          margin: auto;
        }
      }

      &__text {
        font-size: rem-calc(12px);
      }
    }

    @include xlarge-up {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;

      &__container {
        display: flex;
        justify-content: space-between;
        align-items: center;

        > ul {
          display: flex;
          justify-content: center;
          align-items: center;
          height: rem-calc(96px);
          margin-bottom: 0;
          list-style-type: none;

          li {
            position: relative;
          }
        }

        * {
          margin-top: 0;
        }

        .search-icon {
          margin-left: var(--spacings-s);
          vertical-align: middle;
          align-self: center;
          cursor: pointer;
        }
      }

      .submenu__container {
        background: var(--colors-basic-white);
        display: none;
        position: absolute;
        gap: rem-calc(20px);
        left: 50%;
        transform: translateX(-50%);
        min-width: rem-calc(200px);
        padding: 1rem;

        @include copy-secondary();
        @include shadow-sm;

        /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
        clip-path: inset(0 -12px -12px -12px);
        margin-top: 0;
        margin-bottom: 0;
        animation-name: slideIn;
        animation-duration: 0.4s;

        .submenu {
          display: flex;
          flex-direction: column;
          gap: 4px;

          rcm-text-link {
            padding: rem-calc(12px);
          }
        }
      }

      @keyframes slideIn {
        0% {
          transform: scaleY(0) translateX(-50%);
          transform-origin: 100% 0%;
        }

        100% {
          transform: scaleY(1) translateX(-50%);
          transform-origin: 100% 0%;
        }
      }

      rcm-container.submenu_wrapper {
        display: flex;
        padding: rem-calc(37px) 0;

        .submenu {
          display: flex;
          flex-direction: column;
          padding-right: var(--spacings-m);

          rcm-text-link {
            margin-bottom: rem-calc(22px);

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    &__sub {
      background-color: var(--colors-basic-white);
      border: 1px solid var(--colors-grey-medium);
      position: absolute;
      left: 0;
      right: 0;

      @include xlarge-up() {
        top: 150px;
      }

      &--hidden {
        top: -10000px;
        left: -10000px;
      }
    }

    &__search {
      padding: var(--spacings-m) 0;

      @include xlarge-up() {
        padding: var(--spacings-l) 0;
      }

      rcm-columns {
        align-items: center;

        @include breakpoint($large) {
          align-items: start;
        }
      }

      rcm-column {
        margin-bottom: 0;

        &:last-of-type {
          display: flex;
          justify-content: flex-end;
          align-items: center;

          @include xlarge-up {
            margin-bottom: 1.5rem;
          }

          .search__button {
            &-desktop {
              margin-right: var(--spacings-s);
              display: none;

              @include xlarge-up {
                display: block;
              }
            }

            &-mobile {
              margin-right: var(--spacings-xs);
              padding: var(--spacings-xs);

              @include xlarge-up {
                display: none;
              }
            }
          }
        }
      }

      input {
        width: 100%;
        border: none;
        color: var(--colors-dark-base);
        font-size: rem-calc(24px);
        line-height: rem-calc(30px);

        @include breakpoint($large) {
          font-size: 1.5rem;
          line-height: 2.5rem;
        }

        /* Remove the clear (X) icon in WebKit browsers (Chrome, Safari, Edge) */
        &::-webkit-search-cancel-button {
          -webkit-appearance: none;
          appearance: none;
        }

        /* Remove the clear (X) icon in Firefox */
        &::-moz-search-clear-button {
          display: none;
        }

        /* Remove the clear (X) icon in Internet Explorer */
        &::-ms-clear {
          display: none;
        }
      }
    }
  }

  .navigation__main {
    display: none;
    position: absolute;
    left: 0;
    right: 0;

    rcm-container {
      padding: 0;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }

    height: $navigation-main-height-desktop;

    @include layer-highest-priority;

    @include xlarge-up {
      height: rem-calc(96px);
      display: block;
      background-color: var(--colors-basic-white);

      @include layer-highest-priority;
    }
  }

  .navShadow {
    @include shadow-sm;
  }

  .navigation--mobile {
    height: $navigation-main-height-mobile;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: rem-calc(20px);

    @include xlarge-up() {
      display: none;
    }

    @include shadow-sm;

    .open {
      cursor: pointer;
    }

    .close {
      margin-top: 0;
      cursor: pointer;
    }

    &__search-icon {
      margin-right: var(--spacings-m);
      cursor: pointer;
    }

    &__logo-container {
      display: flex;
      align-items: center;
    }

    &__icons {
      display: flex;
      align-items: center;
    }
  }

  .mobile-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(100vh - #{$navigation-main-height-mobile});
    box-sizing: border-box;

    @include xlarge-up {
      display: none;
    }

    &__items {
      padding: rem-calc(21px) rem-calc(20px) rem-calc(21px) rem-calc(20px);

      rcm-button {
        width: 100%;
      }
    }

    &__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      border-bottom: $navigation-border;

      > div {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
      }

      rcm-button {
        font-size: rem-calc(16px);
        line-height: rem-calc(26px);
        font-family: $font-family-semibold;
        color: var(--colors-dark-base);
        margin: 0.5rem 0;
        text-transform: uppercase;
      }
    }

    &__bottom-nav {
      display: flex;
      flex-direction: column;
      gap: rem-calc(16px);
      padding: rem-calc(21px) rem-calc(20px) rem-calc(146px) rem-calc(20px);
      background-color: var(--colors-grey-background);
    }

    .mobile-container__links {
      width: 100vw;
      display: none;
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 1.25rem;

      rcm-text-link {
        font-size: rem-calc(16px);
        line-height: rem-calc(26px);
        margin: rem-calc(10px);
        color: var(--colors-dark-light);
        font-family: $font-family-semibold;
      }
    }
  }
}
